import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import type { RSFundPageModel } from '../../../../types/server-data/ClientConfig';
import { getAssetClassReportData } from '../assetClass.routes';

/**
 * Legacy RS-HG stored asset-class payloads arrive as a full {@link RSFundPageModel},
 * a one-element array `[payload]`, or a bare report body. Until the service
 * guarantees one shape we rebuild stubs with `as RSFundPageModel` — do not
 * “clean this up” by deleting the assertions without a coordinated rs-hg change
 * or you will break older cached / proxied responses.
 *
 * TODO(rs-hg): Normalise `stored_asset_class_report` /
 * `most_recently_stored_asset_class_report` to a single documented JSON shape
 * (full page model + schema) so this can become validation-only.
 */
function parseLegacyAssetClassStoredReportWireResponse<TData>(
  raw: unknown,
  isReportData: (value: unknown) => value is TData,
): RSFundPageModel<TData> | null {
  if (raw == null) return null;

  if (Array.isArray(raw)) {
    const first = raw[0];
    if (typeof first === 'string' && first === 'data not available') {
      return null;
    }
    if (first && typeof first === 'object' && !Array.isArray(first)) {
      const o = first as Record<string, unknown>;
      if (typeof o.error === 'string' && o.error) throw new Error(o.error);
      if ('no data' in o) return null;
      if (isReportData(first)) {
        return {
          selected_position_date: readSelectedPositionDate(first as object),
          data: first,
        } as RSFundPageModel<TData>;
      }
    }
    return null;
  }

  if (typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    if (typeof o.error === 'string' && o.error) throw new Error(o.error);
    if ('no data' in o) return null;

    if (o.data && typeof o.data === 'object' && isReportData(o.data)) {
      return raw as RSFundPageModel<TData>;
    }

    if (isReportData(raw)) {
      return {
        selected_position_date: readSelectedPositionDate(raw as object),
        data: raw,
      } as RSFundPageModel<TData>;
    }
  }

  return null;
}

function readSelectedPositionDate(obj: object): string {
  const v = (obj as Record<string, unknown>).selected_position_date;
  return typeof v === 'string' ? v : '';
}

/** Inner `data` field from a fund page model, when present. */
export function extractAssetClassReportData<T>(
  page: RSFundPageModel<T> | null | undefined,
): T | null {
  const inner = page?.data;
  if (!inner || typeof inner !== 'object') return null;
  return inner as T;
}

export interface UseAssetClassReportPageModelParams<T> {
  fundId?: string;
  reportKey?: string;
  positionDate?: string;
  isReportData: (raw: unknown) => raw is T;
}

export function useAssetClassReportPageModel<T>(
  params: UseAssetClassReportPageModelParams<T>,
  options?: Omit<
    UseQueryOptions<RSFundPageModel<T> | null, Error>,
    'queryKey' | 'queryFn'
  >,
) {
  const { isReportData, ...restParams } = params;

  return useQuery<RSFundPageModel<T> | null, Error>({
    queryKey: [
      'asset-class-report',
      restParams.fundId,
      restParams.reportKey,
      restParams.positionDate,
    ],
    queryFn: () =>
      getAssetClassReportData<T>({
        fundId: restParams.fundId!,
        reportKey: restParams.reportKey!,
        positionDate: restParams.positionDate,
        parseResponse: (raw) =>
          parseLegacyAssetClassStoredReportWireResponse(raw, isReportData),
      }),
    enabled:
      !!restParams.fundId &&
      !!restParams.reportKey &&
      (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}
